import './product-form-v2.scss';
import { ProductCalendar } from './product-calendar/product-calendar';
import { useContext, useEffect, useState } from 'react';
import { QuantityInput } from './quantity-input/quantity-input';
import { DateTime } from 'luxon';
import { Spinner } from '../../components/spinner/spinner';
import { Location } from '../../clients/quipli-service/location/location';
import { useCustomerAPI } from '../../clients/customer-api/customer-api-client';
import { OrderType } from '../../clients/quipli-service/order/order';
import { displayMoneyFormat } from '../../util/util';
import {
  CalculatePriceRequest,
  CalculateRentalPriceRequest,
  isErrorCalculatePriceResult,
} from '../../clients/quipli-service/pricing/pricing';
import { ecommerceToast } from '../../util/toasts';
import { Typography } from '../../components/typography/typography';
import { Radio } from '../../components/radio/radio';
import { Button } from '../../components/form/button';
import { EcommerceProduct, EcommerceUser, ProductAddonSelection } from '../products/product-details/product-details';
import {
  CustomerProduct,
  ProductCustomService,
  ProductOption,
} from '../../clients/customer-api/product/customer-product';
import { Configuration } from '../../clients/quipli-service/configuration/configuration';
import { Dictionary } from 'lodash';
import { Label } from '../../clients/quipli-service/label/label';
import { durationToHuman } from '../../util/date-time-util';
import { OptionSelection } from '../products/customizations';
import { TenantContext } from '../../context/tenant-context';
import { Modal } from '../../components/modal/modal';
import Icon from '../../components/icons/icon';
import { OfflineOrderModal } from '../offline-form/offline-form';
import { StripeContextProvider } from '../../context/stripe-context';
import { PayLaterPlanMessaging } from '../payment/pay-later-plan-messaging/pay-later-plan-messaging';
import { useCustomerYorentClient } from '../../clients/customer-api/customer-yorent-api-client';
import { ProductTrackingType } from '../../clients/quipli-service/product/product';

export const ProductFormV2 = ({
  product,
  location,
  orderType,
  addons,
  services,
  options,
  hidePrices,
  user,
  configs,
  labels,
  setOrderType,
  tagDiscount,
  rentalDates,
  setRentalDates,
}: {
  product: EcommerceProduct;
  location: Location;
  orderType: OrderType;
  addons: ProductAddonSelection;
  services: {
    [serviceId: number]: ProductCustomService & {
      quantity?: number;
      selectedOption?: { label: string; value: string };
    };
  };
  options: { [optionId: number]: { option: ProductOption; selection?: OptionSelection } };
  hidePrices?: boolean;
  user: EcommerceUser;
  configs: Dictionary<Configuration>;
  labels: Dictionary<Label>;
  setOrderType: (orderType: OrderType) => void;
  tagDiscount: number;
  rentalDates: { start?: DateTime; end?: DateTime };
  setRentalDates: (dates: { start?: DateTime; end?: DateTime }) => void;
}) => {
  const { contextPath } = useContext(TenantContext);
  const { getRentalAvailability, calculatePrices, addItemToCart, getMultipleLabels, getProductById } = useCustomerAPI();
  const { refreshCartProductCount } = useCustomerYorentClient();
  const [quantity, setQuantity] = useState<string>();
  const [quantityNumber, setQuantityNumber] = useState<number>();
  const [hasAvailability, setHasAvailability] = useState<boolean>();
  const [rentalPrice, setRentalPrice] = useState<number>();
  const [salePrices, setSalePrices] = useState<{ [productId: number]: number }>();
  const [productSalePrice, setProductSalePrice] = useState<number>(0);
  const [showItemsUnavailableModal, setShowItemsUnavailableModal] = useState(false);
  const [unavailableItems, setUnavailableItems] = useState<{ name: string; quantity: number }[]>([]);
  const [showOfflineForm, setShowOfflineForm] = useState(false);
  const [noAvailabilityLabel, setNoAvailabilityLabel] = useState(
    'No inventory is currently available for this product',
  );

  const isContactForPricing = orderType === OrderType.Rental ? product.hidePrices : product.hideSalePrices;

  useEffect(() => {
    getMultipleLabels(['LBL_NO_INVENTORY_AVAILABLE'])
      .then((lbls) => {
        const noAvailabilityLabel = lbls.find((lbl) => (lbl.key = 'LBL_NO_INVENTORY_AVAILABLE'))?.caption;
        if (noAvailabilityLabel) {
          setNoAvailabilityLabel(noAvailabilityLabel);
        }
      })
      .catch((e) => {
        console.log(e);
      });
  }, []);

  useEffect(() => {
    setQuantityNumber(parseFloat(quantity || ''));
  }, [quantity]);

  const checkHasAvailability = async (prod: CustomerProduct, quantity?: number) => {
    // If product has been deactivated or disabled, display there is no availability.
    // This is to catch any cached product pages that are no longer active.
    if (!prod.isActive) {
      setHasAvailability(false);
      return false;
    }

    if (prod && location && location.checkAvailability && prod.trackingType !== ProductTrackingType.NonInventory) {
      let requestedQuantity = quantity ?? 1;
      if (prod.minimumOrderQuantity && prod.minimumOrderQuantity > requestedQuantity) {
        requestedQuantity = prod.minimumOrderQuantity;
      }

      if (orderType === OrderType.Rental) {
        const availability =
          product.trackingType === ProductTrackingType.NonInventory ||
          !(await getRentalAvailability(product.id, location.id, requestedQuantity)).every(
            (av) => av.start && av.start.diff(DateTime.now().plus(location.maximumLeadTime)).milliseconds > 0,
          );
        if (prod.id === product.id) {
          setHasAvailability(availability);
        }
        return availability;
      } else {
        if (configs['CONF_ECOMMERCE_SALE_REQUIRE_IN_STOCK'].value === '1') {
          const availability =
            product.trackingType === ProductTrackingType.NonInventory ||
            requestedQuantity <=
              (product.stock?.find((pls) => pls.locationId === location.id)?.saleStock || product.saleStock);

          if (prod.id === product.id) {
            setHasAvailability(availability);
          }

          return availability;
        } else {
          return true;
        }
      }
    }
  };

  const rentalDuration =
    rentalDates.start && rentalDates.end ? durationToHuman(rentalDates.end.diff(rentalDates.start)) : undefined;
  const isValidRentalDates = () =>
    rentalDates.start &&
    rentalDates.end &&
    rentalDates.start < rentalDates.end &&
    product &&
    (product.minRentalDuration ? rentalDates.end.diff(rentalDates.start) >= product.minRentalDuration : true) &&
    (product.maxRentalDuration ? rentalDates.end.diff(rentalDates.start) <= product.maxRentalDuration : true);

  const reset = () => {
    setQuantity(undefined);
    setHasAvailability(true);
    setRentalPrice(undefined);
    setSalePrices(undefined);
    setProductSalePrice(0);
    setShowItemsUnavailableModal(false);
    setUnavailableItems([]);
  };

  useEffect(() => {
    reset();
    checkHasAvailability(product, quantityNumber);
  }, [product, location, orderType]);

  useEffect(() => {
    if (quantity === undefined && product) {
      setQuantity(product.recommendedQuantity.toString());
    }
  }, [product, quantity]);

  useEffect(() => {
    if ((configs['CONF_SHOW_PRICE_FOR_RENTAL_DURATION']?.value ?? '0') === '0' || (hidePrices && !user.isLoggedIn)) {
      setRentalPrice(undefined);
    } else if (product && location && orderType === OrderType.Rental && isValidRentalDates()) {
      const addonsList = Object.values(addons).filter((a) => a.selected);
      const productPriceReq = (
        orderType === OrderType.Rental
          ? {
              productId: product.id,
              type: OrderType.Rental,
              start: rentalDates.start,
              end: rentalDates.end,
              locationId: location.id,
              userId: user.userId,
            }
          : {
              productId: product.id,
              type: OrderType.Sale,
              locationId: location.id,
              userId: user.userId,
            }
      ) as CalculatePriceRequest;
      const reqs: CalculatePriceRequest[] = [
        productPriceReq,
        ...addonsList.map((addon) => {
          return {
            productId: addon.id,
            type: orderType === OrderType.Rental && addon.allowRent ? OrderType.Rental : OrderType.Sale,
            locationId: location.id,
            userId: user.userId,
            start: rentalDates.start!,
            end: rentalDates.end!,
          } as CalculateRentalPriceRequest;
        }),
      ];
      calculatePrices(reqs).then((res) => {
        let err;
        let total = 0;
        for (const price of res) {
          if (isErrorCalculatePriceResult(price)) {
            err = price;
            break;
          }
          const q =
            price.productId === product.id
              ? quantityNumber
              : addonsList.find((a) => a.id === price.productId)?.quantity ?? 0;
          total += price.totalPrice * q!;
        }
        if (err) {
          console.error(err.error);
        } else {
          setRentalPrice(total);
        }
      });
    }
  }, [rentalDates, product, location, addons, quantityNumber, orderType]);

  useEffect(() => {
    if (salePrices && orderType === OrderType.Sale) {
      const servicesPrice = Object.values(services).reduce((acc, service) => {
        return acc + service.price * (service.selectedOption ? 1 : service.quantity ?? 0);
      }, 0);
      setProductSalePrice(
        Object.entries(salePrices).reduce((acc, price) => {
          if (parseInt(price[0]) === product.id) {
            return acc + price[1] * (quantityNumber ?? 0);
          } else if (addons[parseInt(price[0])]?.selected) {
            return acc + price[1] * (addons[parseInt(price[0])].quantity ?? 0);
          } else {
            return acc;
          }
        }, 0) /
          100 +
          servicesPrice,
      );
    }
  }, [salePrices, addons, services, quantityNumber, orderType]);

  // fetch all sale prices one time
  useEffect(() => {
    if (product && location) {
      const salePrices: { [productId: number]: number } = {};
      let priceReqs: CalculatePriceRequest[] = [];
      if (product.allowSale) {
        // all add ons will be sale items, so fetch all sale prices at once
        priceReqs = Object.values(addons)
          .filter((a) => a.allowSale)
          .map((a) => {
            return {
              productId: a.id,
              type: OrderType.Sale,
              locationId: location.id,
              userId: user.userId,
            } as CalculatePriceRequest;
          })
          .concat({
            productId: product.id,
            type: OrderType.Sale,
            locationId: location.id,
            userId: user.userId,
          });
      } else if (product.allowRent) {
        // only need sale prices for add ons that don't allow rentals
        priceReqs = Object.values(addons)
          .filter((a) => !a.allowRent)
          .map((a) => {
            return {
              productId: a.id,
              type: OrderType.Sale,
              locationId: location.id,
              userId: user.userId,
            } as CalculatePriceRequest;
          });
      }
      if (priceReqs.length > 0) {
        calculatePrices(priceReqs).then((res) => {
          for (const price of res) {
            if (!isErrorCalculatePriceResult(price)) {
              salePrices[price.productId] = price.totalPrice;
            }
          }
          setSalePrices(salePrices);
        });
      }
    }
  }, [product, location, orderType]);

  const doAddToCart = async (bypassAddonAvailabilityCheck: boolean = false) => {
    if (user && product && location && quantity) {
      try {
        let addonsList =
          orderType === OrderType.Rental
            ? Object.values(addons).filter((a) => a.selected)
            : Object.values(addons)
                .filter((a) => a.allowSale)
                .filter((a) => a.selected);
        if (addonsList.length > 0) {
          const unavailableItems: { id: number; name: string; quantity: number }[] = [];
          addonsList.forEach((addon) => {
            if (!addon.isAvailable) {
              unavailableItems.push({
                id: addon.id,
                name: addon.name,
                quantity: addon.maxQuantityAvailable ?? 0,
              });
            }
          });
          setUnavailableItems(unavailableItems);
          if (unavailableItems.length > 0 && bypassAddonAvailabilityCheck) {
            addonsList = addonsList.filter((a) => !unavailableItems.find((u) => u.id === a.id));
          } else if (unavailableItems.length > 0) {
            setShowItemsUnavailableModal(true);
            return;
          }
        }
        const optionsSelected: { [optionId: string]: number } = {};
        Object.entries(options).forEach(([optionId, selection]) => {
          if (selection.selection) optionsSelected[optionId] = selection.selection.id;
        });
        const servicesSelected = Object.values(services).map((service) => {
          return { id: service.id, value: service.selectedOption?.value ?? service.quantity };
        });

        let req: any = {
          productId: product.id,
          type: orderType,
          quantity: quantity,
          options: optionsSelected,
          skipAvailabilityCheck: product && product.trackingType === ProductTrackingType.NonInventory ? true : false,
          shippingType: undefined,
        };
        if (orderType === OrderType.Rental && rentalDates.start && rentalDates.end) {
          req = { ...req, startDate: rentalDates.start, endDate: rentalDates.end };
        } else if (orderType === OrderType.Sale) {
          req = {
            ...req,
            services: servicesSelected,
          };
        } else {
          console.error(
            `Failed to add product to cart ${orderType} ${rentalDates.start?.toISO()} ${rentalDates.end?.toISO()}`,
          );
          return; // ecommerceToast error just to be safe? although this should never happen
        }
        let cart = await addItemToCart(location.id, req);
        for (const addon of addonsList) {
          const addOnProduct = await getProductById(addon.id);

          let addonReq: any = {
            productId: addon.id,
            type: addon.type,
            quantity: addon.quantity,
            skipAvailabilityCheck:
              addOnProduct && addOnProduct.trackingType === ProductTrackingType.NonInventory ? true : false,
          };
          if (orderType === OrderType.Rental && addon.allowRent) {
            addonReq = { ...addonReq, startDate: rentalDates.start, endDate: rentalDates.end };
          }
          cart = await addItemToCart(location.id, addonReq);
        }
        if (typeof window.dataLayer !== 'undefined') {
          window.dataLayer.push({
            event: 'add_to_cart',
            ecommerce: {
              items: cart.items.map((ci) => {
                return {
                  item_id: ci.product.id,
                  item_name: ci.product.name,
                  quantity: ci.quantity,
                };
              }),
            },
          });
        }
        await refreshCartProductCount();
        ecommerceToast('Item(s) added to cart', 'success');
        return true;
      } catch (e: any) {
        console.error(e);
        ecommerceToast(e.message ? e.message : 'Error adding item(s) to cart', 'error');
      }
    }
    return false;
  };

  const addToCart = async () => {
    if (isContactForPricing) {
      setShowOfflineForm(true);
      return;
    }

    const result = await doAddToCart();
    if (result) {
      window.location.pathname = `${contextPath}cart/display`;
    }
  };

  const rentOrBuyNow = async () => {
    if (location.isOffline || isContactForPricing) {
      setShowOfflineForm(true);
      return;
    } else {
      const result = await doAddToCart();
      //  navigate to checkout
      if (result) {
        window.location.pathname = `${contextPath}cart/checkout`;
      }
    }
  };

  const isDisabled = !hasAvailability || !!Object.values(options).find((o) => o.option.required && !o.selection);

  return product && location ? (
    <div className="product-form-v2 p-4">
      <Typography type="text-md">
        {product.allowRent && product.allowSale ? 'Rent or buy now' : product.allowRent ? 'Rent now' : 'Buy now'}
      </Typography>

      {product.allowRent && product.allowSale && (
        <div className="rent-or-buy-selector flex gap-3 my-1">
          <Radio
            type="ecommercePrimary"
            className="rounded-gray-border p-2.5 w-full"
            label="I want to rent"
            isChecked={orderType === OrderType.Rental}
            onChange={() => setOrderType(OrderType.Rental)}
          />
          <Radio
            type="ecommercePrimary"
            className="rounded-gray-border p-2.5 w-full"
            label="I want to buy"
            isChecked={orderType === OrderType.Sale}
            onChange={() => setOrderType(OrderType.Sale)}
          />
        </div>
      )}

      {orderType === OrderType.Rental ? (
        hasAvailability ? (
          <div className="product-form">
            <ProductCalendar
              product={product}
              quantity={quantityNumber || 1}
              location={location}
              setRentalDates={setRentalDates}
            />
            <div className="quantity-price-wrapper">
              <QuantityInput
                name="rental_quantity"
                value={quantity}
                min={product?.minimumOrderQuantity}
                max={
                  configs['CONF_MAX_ITEMS_PER_ORDER']
                    ? Math.min(product?.maxQuantityAvailable, +configs['CONF_MAX_ITEMS_PER_ORDER'].value)
                    : product?.maxQuantityAvailable
                }
                increment={product?.quantityIncrement}
                onChange={setQuantity}
              />

              {(configs['CONF_SHOW_PRICE_FOR_RENTAL_DURATION']?.value ?? '0') !== '0' && (
                <div className="rental-price-wrapper items-center">
                  <div className="rental-duration">
                    <span>Price {rentalDuration ? `for ${rentalDuration}` : 'N/A'}</span>
                  </div>

                  <span className="rental-price">
                    {product.hidePrices
                      ? 'Contact for pricing'
                      : hidePrices && !user.isLoggedIn
                      ? 'Sign in to see prices'
                      : rentalPrice
                      ? displayMoneyFormat((rentalPrice * tagDiscount) / 100, location.currency)
                      : 'N/A'}
                  </span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="product-form no-inventory">
            {noAvailabilityLabel.replace('{locationPhone}', ' ' + location.phone || '')}
          </div>
        )
      ) : (
        <div className="product-form">
          <div className="quantity-price-wrapper">
            <QuantityInput
              name="sale_quantity"
              value={quantity}
              min={product?.minimumOrderQuantity}
              max={product?.stock?.find((s) => s.locationId === location.id)?.saleStock}
              increment={product?.quantityIncrement}
              onChange={setQuantity}
            />
            <div>
              <Typography type="text-sm">Price</Typography>
              <Typography type="text-md" weight="bold">
                {product.hideSalePrices
                  ? 'Contact for pricing'
                  : hidePrices && !user.isLoggedIn
                  ? 'Sign in to see prices'
                  : salePrices && salePrices[product.id]
                  ? displayMoneyFormat(productSalePrice * tagDiscount, location.currency)
                  : 'N/A'}
              </Typography>
            </div>
          </div>
        </div>
      )}

      {configs['CONF_ENABLE_AFFIRM']?.value === '1' && !hidePrices && rentalPrice && (
        <>
          {product.allowRent && orderType === OrderType.Rental && (
            <StripeContextProvider>
              <PayLaterPlanMessaging locationId={location.id} amount={(rentalPrice! * tagDiscount) / 100} />
            </StripeContextProvider>
          )}
        </>
      )}

      {configs['CONF_ECOMMERCE_SALE_REQUIRE_IN_STOCK']?.value === '1' &&
        configs['CONF_SHOW_UNITS_AVAILABLE']?.value === '1' &&
        orderType === OrderType.Sale &&
        product.trackingType != ProductTrackingType.NonInventory && (
          <Typography type="text-sm" className="mt-2">
            Available quantity:{' '}
            {product.stock?.find((pls) => pls.locationId === location.id)?.saleStock || product.saleStock}
          </Typography>
        )}

      {configs['CONF_SHOW_ADD_CART_BUTTON']?.value === '1' && (
        <Button
          className={`button border-[var(--primary-color-500)] bg-[var(--primary-color-500)] text-white w-full mt-3.5 ${
            isDisabled ? 'disabled' : ''
          }`}
          disabled={isDisabled}
          onClick={() => addToCart()}
        >
          {labels['M_Add_to_Cart']?.caption ?? 'Add to Cart'}
        </Button>
      )}

      <Button
        className={`button border-[var(--secondary-color-500)] bg-[var(--secondary-color-500)] text-white w-full mt-2.5 ${
          isDisabled ? 'disabled' : ''
        }`}
        disabled={isDisabled}
        onClick={() => rentOrBuyNow()}
        data-cy="rent_or_buy_now"
      >
        {orderType === OrderType.Rental
          ? labels['L_RENT_NOW']?.caption ?? 'Rent Now'
          : labels['L_BUILD_PRODUCT']?.caption ?? 'Buy Now'}
      </Button>

      {showItemsUnavailableModal && (
        <ItemsUnavailableModal
          isOpen={showItemsUnavailableModal}
          close={() => setShowItemsUnavailableModal(false)}
          items={unavailableItems}
          addToCart={() => {
            if (location?.isOffline) {
              setShowOfflineForm(true);
            } else {
              doAddToCart(true).then(() => setShowItemsUnavailableModal(false));
            }
          }}
        />
      )}

      <OfflineOrderModal
        isOpen={showOfflineForm}
        close={() => setShowOfflineForm(false)}
        product={product}
        location={location}
        services={Object.values(services)}
        quantity={quantityNumber ?? 1}
        rentalStart={rentalDates.start ?? DateTime.now()}
        rentalEnd={rentalDates.end ?? DateTime.now()}
        orderType={orderType}
      />
    </div>
  ) : (
    <Spinner />
  );
};

const ItemsUnavailableModal = ({
  isOpen,
  items,
  close,
  addToCart,
}: {
  isOpen: boolean;
  items: { name: string; quantity: number }[];
  close: () => void;
  addToCart: () => void;
}) => {
  return (
    <Modal isOpen={isOpen} close={close} headerIcon={<Icon icon="CircleExclamation" />}>
      <div className="items-unavailable-modal">
        <Typography className="text-center" type="text-md" weight="semi-bold">
          Unavailable items will be dropped
        </Typography>
        <h5 className="mt-4">
          <b>The following items are currently unavailable and will be dropped from you cart:</b>
        </h5>
        <ul className="my-4">
          {items.map((item) => (
            <li className="mt-2" key={item.name}>
              <Typography type="text-sm">
                {item.name} ({item.quantity} available)
              </Typography>
            </li>
          ))}
        </ul>
        <Typography type="text-md" weight="semi-bold">
          Would you like to proceed?
        </Typography>
        <div className="flex justify-end gap-4 mt-4">
          <button className="btn button-secondary" onClick={() => close()}>
            Cancel
          </button>
          <button className="btn button-primary" onClick={() => addToCart()}>
            Proceed
          </button>
        </div>
      </div>
    </Modal>
  );
};
